import { useCallback, useEffect, useRef, useState } from "preact/hooks"
import { handleize } from "../helpers/productVariants"

const addToShopifyCart = async (variantId, quantity = 1) => {
    const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded"
        },
        body: `quantity=${quantity}&id=${variantId}`
    })

    if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to add to cart")
    }

    const cartResponse = await fetch("/cart?view=json")
    return cartResponse.json()
}

export const useQuickBuyState = ({ variants, optionIndexes, swatches, product }) => {
    const quickBuyRef = useRef(null)
    const [showQuickBuyOptions, setShowQuickBuyOptions] = useState(false)
    const [selectedSize, setSelectedSize] = useState("")
    const [selectedColorName, setSelectedColorName] = useState("")
    const [selectedColor, setSelectedColor] = useState("")
    const [isAddingToCart, setIsAddingToCart] = useState(false)
    const [hoverColorName, setHoverColorName] = useState("")

    useEffect(() => {
        if (Array.isArray(swatches) && swatches.length > 0 && !selectedColorName) {
            const firstColor = swatches[0]
            setSelectedColorName(firstColor)
            setSelectedColor(handleize(firstColor))
        }
    }, [swatches, selectedColorName])

    useEffect(() => {
        if (!showQuickBuyOptions) return

        const handleClickOutside = (event) => {
            if (quickBuyRef.current && !quickBuyRef.current.contains(event.target)) {
                setShowQuickBuyOptions(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutside)

        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [showQuickBuyOptions])

    useEffect(() => {
        if (!showQuickBuyOptions) return

        const container = quickBuyRef.current
        if (!container) return

        const focusableOption = container.querySelector(".ns-quick-buy-option:not([tabindex='-1'])")

        if (focusableOption && typeof focusableOption.focus === "function") {
            focusableOption.focus()
            return
        }

        if (!container.hasAttribute("tabindex")) {
            container.setAttribute("tabindex", "-1")
        }

        if (typeof container.focus === "function") {
            try {
                container.focus({ preventScroll: true })
            } catch (error) {
                container.focus()
            }
        }
    }, [showQuickBuyOptions])

    const isSizeAvailable = useCallback((sizeName) => {
        if (!sizeName) return false

        return variants.some((variant) => {
            if (!variant?.available) return false

            const sizeMatches = optionIndexes.sizeOptionIndex === -1
                ? false
                : variant[`option${optionIndexes.sizeOptionIndex + 1}`] === sizeName

            if (!sizeMatches) return false

            if (optionIndexes.colorOptionIndex === -1) return true

            const variantColor = variant[`option${optionIndexes.colorOptionIndex + 1}`]
            const variantSlug = handleize(variantColor)

            return variantSlug === selectedColor || variantColor === selectedColorName
        })
    }, [variants, optionIndexes, selectedColor, selectedColorName])

    const findVariantBySizeAndColor = useCallback((size) => {
        return variants.find((variant) => {
            const sizeMatches = optionIndexes.sizeOptionIndex === -1
                ? false
                : variant[`option${optionIndexes.sizeOptionIndex + 1}`] === size

            if (optionIndexes.colorOptionIndex > -1) {
                const variantColor = variant[`option${optionIndexes.colorOptionIndex + 1}`]
                const variantSlug = handleize(variantColor)

                return sizeMatches && (variantSlug === selectedColor || variantColor === selectedColorName)
            }

            return sizeMatches
        })
    }, [variants, optionIndexes, selectedColor, selectedColorName])

    const colorAvailableVariants = useCallback((colorName) => {
        if (optionIndexes.colorOptionIndex === -1) return []

        const targetSlug = handleize(colorName)

        return variants.filter((variant) => {
            if (!variant?.available) return false

            const variantColor = variant[`option${optionIndexes.colorOptionIndex + 1}`]

            if (variantColor === colorName) return true

            return handleize(variantColor) === targetSlug
        })
    }, [variants, optionIndexes])

    const setColorOption = useCallback((colorValue) => {
        if (!colorValue) return

        setSelectedColor(handleize(colorValue))
        setSelectedColorName(colorValue)
        setSelectedSize("")
    }, [])

    const mouseOverColorOption = useCallback((colorValue) => {
        setHoverColorName(colorValue)
    }, [])

    const mouseOutColorOption = useCallback(() => {
        setHoverColorName("")
    }, [])

    const handleQuickBuy = useCallback(async (size) => {
        const variant = findVariantBySizeAndColor(size)
        if (!variant || !isSizeAvailable(size)) return
        if (isAddingToCart) return

        setSelectedSize(size)
        setIsAddingToCart(true)

        try {
            await addToShopifyCart(variant.id, 1)

            if (window.fbq) {
                window.fbq("track", "AddToCart", {
                    value: variant.price.toString(),
                    currency: window.Shopify?.currency?.active || "USD",
                    content_ids: `[${variant.id}]`,
                    content_type: "product"
                })
            }

            console.log("Successfully added to cart:", {
                variantId: variant.id,
                size,
                color: selectedColorName,
                productName: product?.name,
                price: variant.price
            })

            setShowQuickBuyOptions(false)
        } catch (error) {
            console.error("Failed to add to cart:", error)
            alert("Sorry, there was an error adding this item to your cart. Please try again.")
        } finally {
            setIsAddingToCart(false)
        }
    }, [findVariantBySizeAndColor, isSizeAvailable, isAddingToCart, selectedColorName, product])

    return {
        quickBuyRef,
        showQuickBuyOptions,
        setShowQuickBuyOptions,
        selectedSize,
        selectedColorName,
        selectedColor,
        hoverColorName,
        isAddingToCart,
        setColorOption,
        mouseOverColorOption,
        mouseOutColorOption,
        handleQuickBuy,
        isSizeAvailable,
        colorAvailableVariants
    }
}
